import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../services/api';
import { isSpecialList } from '../constants/listTypes';

/**
 * Priority slots management UI, rendered under the Priority Mode toggle in
 * Preferences. Lets the user add ordered slots (each bound to a non-special list),
 * remove them, and reorder them via drag-and-drop. The daily task pick is resolved
 * read-only by the backend on getLists -- this component only edits slot config.
 *
 * `lists` and `initialSlots` are passed in from the parent -- both come from calls
 * the Preferences page already makes (getLists for the Account page, and the slot
 * config bundled into getUserPreferences). So this component makes NO fetch on
 * mount and renders instantly; it only hits the network on user actions
 * (add/remove/reorder), refetching the slots afterward to reconcile.
 *
 * Reordering mirrors the app's task drag-and-drop (native HTML5 DnD, see TodoList).
 */
export default function PrioritySlotsConfig({ lists = [], initialSlots = [] }) {
  const [slots, setSlots] = useState(initialSlots);
  const [selectedListId, setSelectedListId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Non-special lists the user can bind a slot to (derived from the prop -- no fetch).
  const userLists = useMemo(
    () => lists.filter(l => !isSpecialList(l.list_type)),
    [lists]
  );

  const loadSlots = useCallback(async () => {
    const data = await api.getPrioritySlots();
    setSlots(data);
  }, []);

  // Default the dropdown to the first available list once lists are known.
  useEffect(() => {
    if (!selectedListId && userLists.length > 0) {
      setSelectedListId(userLists[0].id);
    }
  }, [userLists, selectedListId]);

  const handleAdd = async () => {
    if (!selectedListId) return;
    setBusy(true);
    setError(null);
    try {
      await api.createPrioritySlot(selectedListId);
      await loadSlots();
    } catch (err) {
      setError(err.message || 'Failed to add priority slot');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (slotId) => {
    setBusy(true);
    setError(null);
    // Optimistically remove
    const previous = slots;
    setSlots(prev => prev.filter(s => s.id !== slotId));
    try {
      await api.deletePrioritySlot(slotId);
    } catch (err) {
      setSlots(previous);
      setError(err.message || 'Failed to remove priority slot');
    } finally {
      setBusy(false);
    }
  };

  // --- Drag-and-drop reorder (mirrors TodoList's native HTML5 DnD) ---
  const handleDragStart = (e, slotId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slotId);
    setDraggingId(slotId);
  };

  const handleDragOver = (e) => {
    // Necessary to allow a drop
    e.preventDefault();
  };

  const handleDragEnter = (slotId) => {
    if (!draggingId || draggingId === slotId) return;
    setDragOverId(slotId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDrop = async (e, targetSlotId) => {
    e.preventDefault();
    const draggedSlotId = e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDragOverId(null);
    if (!draggedSlotId || draggedSlotId === targetSlotId) return;

    const reordered = Array.from(slots);
    const fromIndex = reordered.findIndex(s => s.id === draggedSlotId);
    const toIndex = reordered.findIndex(s => s.id === targetSlotId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    // Optimistically apply, persist, reconcile on failure.
    const previous = slots;
    setSlots(reordered);
    setBusy(true);
    setError(null);
    try {
      await api.reorderPrioritySlots(reordered.map(s => s.id));
    } catch (err) {
      setSlots(previous);
      setError(err.message || 'Failed to reorder priority slots');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      {error && (
        <div className="notification notification-error" style={{ marginBottom: '0.75rem' }}>
          {error}
        </div>
      )}

      {slots.length === 0 ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
          No priority slots yet. Add one below to reserve a spot at the top of To Do Today.
        </p>
      ) : (
        <div style={{ marginBottom: '0.75rem' }}>
          {slots.map((slot, index) => {
            const showDropIndicator = draggingId && dragOverId === slot.id;
            return (
              <React.Fragment key={slot.id}>
                {showDropIndicator && <div className="todo-drop-indicator" />}
                <div
                  className={`todo-item-draggable-wrapper${draggingId === slot.id ? ' todo-item-dragging' : ''}`}
                  draggable={!busy}
                  onDragStart={(e) => handleDragStart(e, slot.id)}
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(slot.id)}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, slot.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    marginBottom: '0.5rem',
                    border: '1px solid var(--border-color, #ccc)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--input-bg, #fff)',
                    cursor: busy ? 'default' : 'grab',
                  }}
                >
                  <span aria-hidden="true" style={{
                    color: 'var(--text-muted)',
                    fontSize: '1rem',
                    lineHeight: 1,
                    userSelect: 'none',
                  }}>
                    ⠿
                  </span>
                  <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    minWidth: '1.5rem',
                  }}>
                    #{index + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                    {slot.list_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemove(slot.id)}
                    disabled={busy}
                    aria-label="Remove priority slot"
                    style={{
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color, #ccc)',
                      backgroundColor: 'transparent',
                      color: 'var(--danger, #d9534f)',
                      fontSize: '1rem',
                      lineHeight: 1,
                      cursor: busy ? 'not-allowed' : 'pointer',
                      opacity: busy ? 0.4 : 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {userLists.length === 0 ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0 }}>
          Create a list first to bind a priority slot to it.
        </p>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <select
            value={selectedListId}
            onChange={(e) => setSelectedListId(e.target.value)}
            disabled={busy}
            style={{
              padding: '0.375rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid var(--border-color, #ccc)',
              backgroundColor: 'var(--input-bg, #fff)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {userLists.map(list => (
              <option key={list.id} value={list.id}>{list.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={busy || !selectedListId}
          >
            Add priority slot
          </button>
        </div>
      )}
    </div>
  );
}
